import { BreadcrumbsProps, DropdownTrigger, Breadcrumbs as HeroBreadcrumbs } from '@heroui/react';
import { twMerge } from 'tailwind-merge';

import { HeroBaseProps } from '../utils';
import { Dropdown, DropdownItem, DropdownMenu } from './Dropdown';

type SortlistBreadcrumbsProps = HeroBaseProps<
  BreadcrumbsProps,
  'maxItems' | 'itemsBeforeCollapse' | 'itemsAfterCollapse' | 'itemClasses'
> & {
  isIconOnly?: boolean;
};

// For all Breadcrumbs props see https://v2.heroui.com/docs/components/breadcrumbs
export const Breadcrumbs = ({ isIconOnly, ...props }: SortlistBreadcrumbsProps) => (
  <HeroBreadcrumbs
    {...props}
    variant="light"
    color="foreground"
    size="sm"
    underline="none"
    separator="/"
    classNames={{
      ...props.classNames,
      base: twMerge('', props.classNames?.base),
      list: twMerge('gap-1 ps-0 my-0', props.classNames?.list),
    }}
    itemClasses={{
      ...props.itemClasses,
      item: twMerge(
        // Clickable items (rendered as <a>): dark text, lighter on hover.
        // Non-clickable stems (no href, not current): greyed out, no hover, no pointer.
        // Current item (data-current=true): dark again to anchor where the user is,
        // but still non-clickable (it has no href so it inherits the grey :hover override).
        'text-sm font-medium text-secondary-950 hover:text-secondary-500',
        '[&:not(a)]:text-secondary-400 [&:not(a)]:cursor-default [&:not(a)]:hover:text-secondary-400',
        'data-[current=true]:text-secondary-950 data-[current=true]:hover:text-secondary-950 data-[current=true]:cursor-default',
        isIconOnly ? 'text-medium text-inherit' : '[&_svg]:text-secondary-500',
        props.itemClasses?.item,
      ),
      separator: twMerge('text-secondary-300 text-sm', props.classNames?.separator),
    }}
    renderEllipsis={({ items, ellipsisIcon, separator }) => (
      <div className="flex items-center gap-1">
        <Dropdown>
          <DropdownTrigger>
            <span className="cursor-pointer text-secondary-950 hover:text-secondary-500">{ellipsisIcon}</span>
          </DropdownTrigger>
          <DropdownMenu aria-label="Collapsed breadcrumbs">
            {items.map((item, index) => (
              <DropdownItem key={index} href={item.href} textValue={String(item.children ?? '')}>
                {item.children}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
        {separator}
      </div>
    )}
  />
);

export { BreadcrumbItem } from '@heroui/react';
